import React from "react";
import { Alert, Button, Input, Modal, message } from "antd";
import { useTranslation } from "react-i18next";
import type { ExternalMutationResult } from "../../../interfaces/External.interface";

interface ApiClientSecretModalProps {
  /** The API client just created, or null when the modal is closed. */
  created: ExternalMutationResult | null;
  /** The heading; defaults to the one for a created client. */
  title?: string;
  /** A line shown under the warning, such as when the old secret stops working. */
  notice?: string;
  onClose: () => void;
}

/**
 * Shows a new API client's credentials, once. LiftCloud returns the secret
 * only in the create response and keeps no copy it can show again, so this
 * modal is the operator's only chance to record it: it says so, offers a
 * copy button for each value, and closes only on an explicit acknowledgement.
 */
function ApiClientSecretModal({
  created,
  title = "",
  notice = "",
  onClose,
}: ApiClientSecretModalProps) {
  const [t] = useTranslation("global");

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(t("apiClients.copied"));
    } catch {
      message.error(t("apiClients.copyFailed"));
    }
  };

  // Unmounted, not merely hidden, once acknowledged: the secret must not stay
  // in the page after the operator has dismissed it.
  if (created === null) return null;

  const clientId = created.client_id ?? created.id ?? "";
  const secret = created.client_secret ?? "";

  return (
    <Modal
      open
      title={title || t("apiClients.createdTitle")}
      closable={false}
      maskClosable={false}
      keyboard={false}
      onCancel={onClose}
      footer={[
        <Button key="done" type="primary" data-testid="api-client-secret-done" onClick={onClose}>
          {t("apiClients.done")}
        </Button>,
      ]}
    >
      <div data-testid="api-client-secret-modal">
        {secret ? (
          <Alert type="warning" showIcon message={t("apiClients.secretWarning")} />
        ) : (
          <Alert type="error" showIcon message={t("apiClients.noSecret")} />
        )}
        {notice && <Alert type="info" showIcon message={notice} className="mt-3" />}
        <div className="mt-3">
          <span className="drawer__text">{t("apiClients.clientId")}</span>
          <Input.Group compact style={{ display: "flex", marginTop: 6 }}>
            <Input readOnly value={clientId} data-testid="api-client-secret-client-id" />
            <Button data-testid="api-client-copy-client-id" onClick={() => copy(clientId)}>
              {t("apiClients.copy")}
            </Button>
          </Input.Group>
        </div>
        {secret && (
          <div className="mt-3">
            <span className="drawer__text">{t("apiClients.clientSecret")}</span>
            <Input.Group compact style={{ display: "flex", marginTop: 6 }}>
              <Input readOnly value={secret} data-testid="api-client-secret-value" />
              <Button data-testid="api-client-copy-secret" onClick={() => copy(secret)}>
                {t("apiClients.copy")}
              </Button>
            </Input.Group>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ApiClientSecretModal;
